import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked
} from "@angular/core";

import { OfflineSyncService } from "../../offline/offline-sync.service";
import { OfflineSyncSummary } from "../../offline/offline-mode.types";

const AUTO_DISMISS_MS = 4_000;

interface SyncToastView {
  tone: "progress" | "success" | "error";
  icon: string;
  title: string;
  detail: string;
}

/**
 * DEV-966 — toast da sincronização: aparece quando a fila começa a drenar (ao reabrir o app,
 * voltar a rede ou ao primeiro plano), acompanha o progresso e fecha sozinho no sucesso. Em erro
 * fica até a pessoa fechar — o banner segue mostrando "Tentar de novo / Descartar".
 */
@Component({
  selector: "app-offline-sync-toast",
  standalone: true,
  templateUrl: "./offline-sync-toast.component.html",
  styleUrl: "./offline-sync-toast.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfflineSyncToastComponent {
  private readonly sync = inject(OfflineSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dismissedAt = signal<number>(0);
  // Handle do auto-dismiss; signal só para atender o contrato de Signals do hook, nunca é lido em template.
  private readonly timer = signal<ReturnType<typeof globalThis.setTimeout> | null>(null);

  public readonly view = computed<SyncToastView | null>(() => {
    const summary = this.sync.summary();
    if (summary.phase === "idle" || summary.changedAt <= this.dismissedAt()) return null;
    return buildView(summary);
  });

  constructor() {
    effect(() => {
      const summary = this.sync.summary();
      if (summary.phase !== "done") return;
      // untracked: o efeito depende só do summary; ler/escrever o handle não pode reagendá-lo.
      untracked(() => {
        this.clearTimer();
        this.timer.set(globalThis.setTimeout(() => this.dismiss(), AUTO_DISMISS_MS));
      });
    });
    this.destroyRef.onDestroy(() => this.clearTimer());
  }

  /** Registros recusados (4xx) só saem da fila por decisão da pessoa: reenviar ou descartar. */
  public readonly canRecover = computed<boolean>(
    () => this.sync.failedCount() > 0 && !this.sync.isSyncing() && this.view()?.tone === "error"
  );

  public onRetry(): void {
    void this.sync.retryFailed();
  }

  public onDiscard(): void {
    void this.sync.discardFailed().then(() => this.dismiss());
  }

  public dismiss(): void {
    this.clearTimer();
    this.dismissedAt.set(Date.now());
    this.sync.dismissSummary();
  }

  private clearTimer(): void {
    const handle = this.timer();
    if (handle) globalThis.clearTimeout(handle);
    this.timer.set(null);
  }
}

function plural(count: number, singular: string, pluralWord: string): string {
  return `${count} ${count === 1 ? singular : pluralWord}`;
}

function buildView(summary: OfflineSyncSummary): SyncToastView {
  const registros = (count: number): string => plural(count, "registro", "registros");

  if (summary.phase === "syncing") {
    return {
      tone: "progress",
      icon: "uil uil-sync",
      title: `Sincronizando ${registros(summary.total)}…`,
      detail: `${summary.sent} de ${summary.total} enviados`
    };
  }

  if (summary.phase === "done") {
    return {
      tone: "success",
      icon: "uil uil-check-circle",
      title: `${registros(summary.sent)} ${summary.sent === 1 ? "enviado" : "enviados"}`,
      detail: "Tudo o que você registrou em sala já está na plataforma."
    };
  }

  const failed =
    summary.failed > 0
      ? `${registros(summary.failed)} recusado${summary.failed === 1 ? "" : "s"}`
      : "";
  const remaining = summary.total - summary.sent - summary.failed;
  const waiting = remaining > 0 ? `${registros(remaining)} aguardando rede` : "";
  return {
    tone: "error",
    icon: "uil uil-exclamation-triangle",
    title: "Sincronização incompleta",
    detail: [failed, waiting].filter(Boolean).join(" · ") || "Tentaremos de novo automaticamente."
  };
}
